import { GET, httpOk, PUT, POST, DELETE } from 'helpers/api';
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import DeleteModal from 'pages/parts/DeleteModal'

const VERSION_META = {
	desktop_client: { label: 'Desktop Kassa', icon: 'uil-desktop', platform: 'desktop' },
	waiter_android: { label: 'Waiter Android', icon: 'uil-android', platform: 'android' },
	waiter_ios: { label: 'Waiter iOS', icon: 'uil-apple', platform: 'ios' },
	android_delivery: { label: 'Delivery Android', icon: 'uil-truck', platform: 'android' },
	android_owner: { label: 'Owner Android', icon: 'uil-store', platform: 'android' },
	ios_owner: { label: 'Owner iOS', icon: 'uil-store', platform: 'ios' },
	ios_delivery: { label: 'Delivery iOS', icon: 'uil-truck', platform: 'ios' },
	android_pos: { label: 'POS Android', icon: 'uil-mobile-android', platform: 'android' },
}

function versionMeta(type) {
	return VERSION_META[type] || { label: type, icon: 'uil-mobile-android', platform: 'other' }
}

function getDefaultRange() {
	return { 'type': 'desktop_client', 'from_version': '', 'to_version': '', 'reason': '', 'is_active': true }
}

function Index() {
	const { t } = useTranslation()

	const [data, setData] = useState([])
	const [isSaving, setIsSaving] = useState(false)

	const [ranges, setRanges] = useState([])
	const [selectedRange, setSelectedRange] = useState(getDefaultRange())
	const [modalRange, setModalRange] = useState(false)
	const [modalDeleteRange, setModalDeleteRange] = useState(false)

	async function save() {
		setIsSaving(true)
		try {
			await PUT('/admin/versions', data, { loader: true })
		} finally {
			setIsSaving(false)
		}
	}

	function updateItem(index, patch) {
		setData(prev => prev.map((item, itemIndex) => (
			itemIndex === index ? { ...item, ...patch } : item
		)))
	}

	async function getData() {
		const response = await GET('/admin/versions', {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data.map(item => ({
				...item,
				required: Boolean(Number(item.required)),
				is_required: Boolean(item.is_required),
			})))
		}
	}

	async function getRanges() {
		const response = await GET('/admin/versions/ranges', {}, { loader: true })
		if (httpOk(response)) setRanges(response.data)
	}

	function toggleModalRange(bool = false, item = getDefaultRange()) {
		setSelectedRange({ ...getDefaultRange(), ...item })
		setModalRange(bool)
	}

	function toggleModalDeleteRange(bool = false, item = getDefaultRange()) {
		setSelectedRange({ ...getDefaultRange(), ...item })
		setModalDeleteRange(bool)
	}

	async function saveRange(e) {
		e.preventDefault()

		const payload = {
			'type': selectedRange.type,
			'from_version': selectedRange.from_version,
			'to_version': selectedRange.to_version,
			'reason': selectedRange.reason || null,
			'is_active': Boolean(selectedRange.is_active),
		}

		var response
		if (selectedRange.id) {
			response = await PUT(`/admin/versions/ranges/${selectedRange.id}`, payload, { loader: true })
		} else {
			response = await POST('/admin/versions/ranges', payload, { loader: true })
		}

		if (httpOk(response)) {
			toggleModalRange()
			getRanges()
		}
	}

	async function deleteRange() {
		const response = await DELETE(`/admin/versions/ranges/${selectedRange.id}`, {}, { loader: true })
		if (httpOk(response)) {
			toggleModalDeleteRange()
			getRanges()
		}
	}

	useEffect(() => {
		getData()
		getRanges()
	}, []);

	return (
		<>
			<div className="page-header d-flex justify-content-between align-items-center">
				<div>
					<h4 className="vertical-center mb-1">
						{t('versions')}
					</h4>
					<p className="text-muted mb-0 fz-14">
						{t('versions_page_hint')}
					</p>
				</div>
				<button
					className="btn btn-primary px-4"
					disabled={isSaving || !data.length}
					onClick={() => save()}>
					<i className="uil uil-check me-1"></i>
					{t('save')}
				</button>
			</div>

			<div className="row g-3">
				{data.map((item, index) => {
					const meta = versionMeta(item.type)

					return (
						<div className="col-12 col-md-6 col-xl-4 col-xxl-3" key={item.id || item.type}>
							<div className="card h-100 shadow-sm border-0">
								<div className="card-body d-flex flex-column gap-3">
									<div className="d-flex align-items-start justify-content-between gap-2">
										<div className="d-flex align-items-center gap-3">
											<div
												className="d-flex align-items-center justify-content-center rounded-circle bg-light"
												style={{ width: 44, height: 44 }}>
												<i className={`${meta.icon} fz24 text-primary`}></i>
											</div>
											<div>
												<div className="fw-semibold">{meta.label}</div>
												<div className="text-muted fz-12">{item.type}</div>
											</div>
										</div>
										<span className={`badge ${meta.platform === 'desktop' ? 'bg-dark' : meta.platform === 'ios' ? 'bg-secondary' : 'bg-success'}`}>
											{meta.platform}
										</span>
									</div>

									<div>
										<label className="form-label mb-1">{t('version')}</label>
										<input
											type="text"
											className="form-control"
											value={item.version ?? ''}
											onChange={(e) => updateItem(index, { version: e.target.value })}
										/>
									</div>

									<div className="border rounded p-3 bg-light-subtle d-flex flex-column gap-2">
										<div className="d-flex justify-content-between align-items-center gap-3">
											<div>
												<div className="fw-medium fz-14">{t('mandatory_update')}</div>
												<div className="text-muted fz-12">required</div>
											</div>
											<div className="form-check form-switch form-switch-md mb-0">
												<input
													type="checkbox"
													className="form-check-input"
													checked={Boolean(item.required)}
													onChange={(e) => updateItem(index, { required: e.target.checked })}
												/>
											</div>
										</div>

										<div className="d-flex justify-content-between align-items-center gap-3">
											<div>
												<div className="fw-medium fz-14">{t('update_is_required')}</div>
												<div className="text-muted fz-12">is_required</div>
											</div>
											<div className="form-check form-switch form-switch-md mb-0">
												<input
													type="checkbox"
													className="form-check-input"
													checked={Boolean(item.is_required)}
													onChange={(e) => updateItem(index, { is_required: e.target.checked })}
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					)
				})}
			</div>

			{!data.length &&
				<div className="card border-0 shadow-sm mt-3">
					<div className="card-body text-center text-muted py-5">
						{t('list_empty')}
					</div>
				</div>
			}

			{/* Точечное принудительное обновление по диапазону версий — в отличие от
				общего "required" выше, не заставляет обновляться всех подряд, а только
				тех, чья версия реально попала в баганный диапазон */}
			<div className="page-header d-flex justify-content-between align-items-center mt-4">
				<div>
					<h4 className="vertical-center mb-1">Принудительное обновление по диапазону версий</h4>
					<p className="text-muted mb-0 fz-14">
						Обязательное обновление только для клиентов, чья версия попала в диапазон —
						остальные обновляются по обычному флагу выше
					</p>
				</div>
				<button className="btn-rounded btn btn-primary" onClick={() => toggleModalRange(true)}>
					Добавить диапазон
				</button>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th>Тип</th>
									<th className="text-center">Версия от</th>
									<th className="text-center">Версия до</th>
									<th>Причина</th>
									<th className="text-center">Активен</th>
									<th style={{ 'minWidth': '140px' }} className="text-end"></th>
								</tr>
							</thead>
							<tbody>
								{ranges.map((item, index) => (
									<tr key={item.id}>
										<td className="text-center">{index + 1}</td>
										<td>{versionMeta(item.type).label}</td>
										<td className="text-center">{item.from_version}</td>
										<td className="text-center">{item.to_version}</td>
										<td className="td-ellipsis" title={item.reason}>{item.reason || '—'}</td>
										<td className="text-center">
											<span className={`badge ${item.is_active ? 'bg-success' : 'bg-secondary'}`}>
												{item.is_active ? 'да' : 'нет'}
											</span>
										</td>
										<td className="text-end">
											<button className="btn btn-sm btn-outline-primary me-2"
												onClick={() => toggleModalRange(true, item)}>
												<i className="uil-edit-alt"></i>
											</button>
											<button className="btn btn-sm btn-outline-danger"
												onClick={() => toggleModalDeleteRange(true, item)}>
												<i className="uil-trash-alt"></i>
											</button>
										</td>
									</tr>
								))}
								{ranges.length === 0 &&
									<tr>
										<td colSpan={7} className="text-center text-muted py-4">Диапазонов нет</td>
									</tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* MODAL: создание/правка диапазона */}
			<Modal show={modalRange} backdrop="static" centered onHide={() => toggleModalRange(false)}>
				<form onSubmit={saveRange}>
					<Modal.Header closeButton>
						<Modal.Title>{selectedRange.id ? 'Диапазон версий' : 'Новый диапазон версий'}</Modal.Title>
					</Modal.Header>
					<Modal.Body className="d-flex flex-column gap-3">
						<div>
							<label className="form-label">Тип приложения</label>
							<select className="form-select" required
								disabled={Boolean(selectedRange.id)}
								value={selectedRange.type}
								onChange={(e) => setSelectedRange({ ...selectedRange, 'type': e.target.value })}>
								{Object.keys(VERSION_META).map((key) => (
									<option key={key} value={key}>{VERSION_META[key].label}</option>
								))}
							</select>
						</div>
						<div className="d-flex gap-3">
							<div className="flex-fill">
								<label className="form-label">Версия от</label>
								<input type="text" className="form-control" required
									placeholder="1.5.259"
									value={selectedRange.from_version}
									onChange={(e) => setSelectedRange({ ...selectedRange, 'from_version': e.target.value })}
								/>
							</div>
							<div className="flex-fill">
								<label className="form-label">Версия до</label>
								<input type="text" className="form-control" required
									placeholder="1.5.274"
									value={selectedRange.to_version}
									onChange={(e) => setSelectedRange({ ...selectedRange, 'to_version': e.target.value })}
								/>
							</div>
						</div>
						<div>
							<label className="form-label">Причина</label>
							<input type="text" className="form-control"
								placeholder="Например: дубли товаров из-за cloud_revision"
								value={selectedRange.reason ?? ''}
								onChange={(e) => setSelectedRange({ ...selectedRange, 'reason': e.target.value })}
							/>
						</div>
						<div className="form-check form-switch">
							<input type="checkbox" className="form-check-input" id="range_is_active"
								checked={Boolean(selectedRange.is_active)}
								onChange={(e) => setSelectedRange({ ...selectedRange, 'is_active': e.target.checked })}
							/>
							<label className="form-check-label" htmlFor="range_is_active">Диапазон активен</label>
						</div>
					</Modal.Body>
					<Modal.Footer>
						<button type="button" className="btn btn-warning" onClick={() => toggleModalRange(false)}>Отмена</button>
						<button type="submit" className="btn btn-primary">Сохранить</button>
					</Modal.Footer>
				</form>
			</Modal>

			<DeleteModal
				modalDelete={modalDeleteRange}
				setModalDelete={setModalDeleteRange}
				deleteItem={deleteRange}
				message={`Удалить диапазон ${selectedRange.from_version}–${selectedRange.to_version} (${versionMeta(selectedRange.type).label})?`}>
			</DeleteModal>
		</>
	)
}

export default Index
